import { ChevronDownIcon } from 'lucide-react';
import * as React from 'react';

import { DimeButton } from '../DimeButton.js';
import { DimeFilterButtonContent } from '../DimeFilterButtonContent.js';
import { DimeFilterList, DimeFilterSelectAllToggle } from '../DimeFilterList/index.js';
import { DimePopover, DimePopoverContent, DimePopoverTrigger } from '../DimePopover.js';
import { openComboboxOnArrowDown } from '../lib/open-combobox-on-arrow-down.js';
import { useFilterSelectAll } from '../lib/use-filter-select-all.js';

import type { DimeFilterComboboxProps } from './DimeFilterComboboxProps.js';

const NO_PICKS: ReadonlyArray<string> = [];
const IGNORE_PICKS = () => undefined;

/** A list toolbar's filter: a button that opens a `DimeFilterList`, naming one pick or counting several. */
export function DimeFilterCombobox(props: DimeFilterComboboxProps) {
  const { label, icon, options, emptyMessage, optionsLabel, notice, hideSearch } = props;
  const [open, setOpen] = React.useState(false);
  const selectAll = useFilterSelectAll({
    options,
    value: props.multiple ? props.value : NO_PICKS,
    onValueChange: props.multiple ? props.onValueChange : IGNORE_PICKS,
    // Only a list holding every option can pick them all, so a server-searched page offers no Select All.
    enabled: props.multiple === true && props.selectAll !== false && props.onSearchChange == null,
    maxPicks: props.multiple ? props.maxPicks : undefined,
  });
  const listRef = React.useRef<HTMLDivElement>(null);
  const shared = {
    ref: listRef,
    label: optionsLabel,
    options,
    emptyMessage,
    notice,
    searchPlaceholder: props.searchPlaceholder,
    search: hideSearch ? ('never' as const) : ('always' as const),
    // The list keeps a small inset from the popover's edges.
    listClassName: 'max-h-72 p-1',
    searchMaxLength: props.searchMaxLength,
    onSearchChange: props.onSearchChange,
    selectedOptions: props.selectedOptions,
  };

  let buttonName: string;
  let buttonContent: React.ReactNode;
  let list: React.ReactNode;
  if (props.multiple) {
    const count = props.value.length;
    buttonName = count > 0 ? `${label}, ${count} applied` : label;
    buttonContent = <DimeFilterButtonContent label={label} icon={icon} count={count} />;
    const toggle = selectAll.offered ? (
      <DimeFilterSelectAllToggle
        label={label}
        pressed={selectAll.allPicked}
        onPressedChange={selectAll.toggleAll}
      />
    ) : null;
    list = (
      <>
        {toggle && hideSearch && (
          <div className="flex items-center justify-between gap-2 border-b px-3 py-1.5">
            <span className="text-xs font-medium text-muted-foreground">{label}</span>
            {toggle}
          </div>
        )}
        <DimeFilterList
          {...shared}
          multiple
          searchAction={hideSearch ? undefined : toggle}
          value={selectAll.value}
          onValueChange={selectAll.onValueChange}
        />
      </>
    );
  } else {
    const { value } = props;
    const picked =
      options.find((option) => option.value === value) ??
      props.selectedOptions?.find((option) => option.value === value);
    // A value missing from the options still filters, so the button names it rather than reading as unfiltered.
    const shownLabel = value === undefined ? label : (picked?.label ?? '1 selected');
    buttonName = value === undefined ? label : `${label}: ${shownLabel}`;
    buttonContent = <DimeFilterButtonContent label={shownLabel} icon={icon} />;
    list = (
      <DimeFilterList
        {...shared}
        value={value}
        onValueChange={props.onValueChange}
        clearable={props.clearable}
        onPick={() => setOpen(false)}
      />
    );
  }

  return (
    <DimePopover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        // A reset elsewhere (Clear filters) sends the same `[]`, so Select All ends when the list closes.
        if (!next) selectAll.reset();
      }}
    >
      <DimePopoverTrigger asChild>
        <DimeButton
          variant="outline"
          size="sm"
          role="combobox"
          aria-expanded={open}
          aria-label={buttonName}
          className="gap-1.5"
          onKeyDown={openComboboxOnArrowDown}
        >
          {buttonContent}
          <ChevronDownIcon
            aria-hidden
            className="pointer-events-none size-4 text-muted-foreground"
          />
        </DimeButton>
      </DimePopoverTrigger>
      <DimePopoverContent
        align="start"
        className="w-auto min-w-[min(18rem,calc(100vw-2rem))] p-0"
        aria-label={optionsLabel}
        // Focus starts in the search box, or on the list without one, rather than on a heading toggle.
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          if (hideSearch) listRef.current?.focus();
          else listRef.current?.querySelector('input')?.focus();
        }}
      >
        {list}
      </DimePopoverContent>
    </DimePopover>
  );
}
